import { PromoAnalyticsService } from '@analytics/feature';
import { AsyncPipe, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { BannersWithTabItem, BannerWithTabItem } from '@promo/const';
import { PromoService } from '@promo/data-access';
import { InViewportDirective } from '@sdvor/shared/ui';
import { NavigationButtonComponent, SdvorSwiperComponent } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2/models';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { SwiperOptions } from 'swiper/types';
import { BannerWithTabGroupComponent } from './banner-with-tab-group/banner-with-tab-group.component';
import { BannerWithTabItemComponent } from './banner-with-tab-item';

@Component({
  selector: 'sd-banners-with-tab',
  imports: [
    AsyncPipe,
    BannerWithTabGroupComponent,
    BannerWithTabItemComponent,
    RouterLink,
    NavigationButtonComponent,
    SdvorSwiperComponent,
    SlicePipe,
    InViewportDirective,
  ],
  templateUrl: './banners-with-tab.component.html',
  styleUrls: ['./banners-with-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannersWithTabComponent implements OnInit {
  protected iconTypes = IconTypes;
  protected config: SwiperOptions = {
    slidesPerView: 'auto',
    navigation: { enabled: false },
    scrollbar: { enabled: false },
    breakpoints: {
      [this.deviceService.sizes.md]: {
        slidesPerView: 4,
        spaceBetween: 20,
        scrollbar: { enabled: false },
        navigation: { enabled: true },
      },
    },
  };

  protected isTabletMdUp$: Observable<boolean> = this.deviceService.isTabletMdUp$;
  protected isDesktopLgUp$: Observable<boolean> = this.deviceService.isDesktopLgUp$;
  protected isLoading$: Observable<boolean> = this.promoService.getPromoLoading();

  protected banners$: Observable<BannersWithTabItem | undefined>;
  protected filteredBanners$: Observable<BannerWithTabItem[]>;

  protected selectedGroup = signal<string | null>(null);
  private selectedGroup$ = toObservable(this.selectedGroup);

  constructor(
    private deviceService: DeviceService,
    private promoService: PromoService,
    private promoAnalyticsService: PromoAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.banners$ = combineLatest({
      promo: this.promoService.getPromo().pipe(filter((promo) => !!promo)),
      isTabletMdUp: this.isTabletMdUp$,
      isDesktopLgUp: this.isDesktopLgUp$,
    }).pipe(
      map(({ promo, isTabletMdUp, isDesktopLgUp }) => {
        if (promo.bannersWithTab) {
          const { label, link, bannerWithTabGroups } = promo.bannersWithTab;

          if (bannerWithTabGroups.length > 0 && !this.selectedGroup()) {
            this.selectedGroup.set(bannerWithTabGroups[0].label);
          }

          return {
            label,
            link,
            bannerWithTabGroups: bannerWithTabGroups.map((bannerGroup) => ({
              label: bannerGroup.label,
              banners: bannerGroup.banners.map((banner) => ({
                ...banner,
                url: isDesktopLgUp ? banner.url.isDesktop : isTabletMdUp ? banner.url.isTablet : banner.url.isMobile,
              })),
            })),
          };
        }

        return undefined;
      }),
    );
    this.filteredBanners$ = combineLatest({
      bannersWithTab: this.banners$,
      selectedGroup: this.selectedGroup$,
    }).pipe(
      map(({ bannersWithTab, selectedGroup }) => {
        if (!bannersWithTab || !bannersWithTab.bannerWithTabGroups.length) {
          return [];
        }

        let banners: BannerWithTabItem[] = [];

        const currentGroup = selectedGroup || bannersWithTab.bannerWithTabGroups[0].label;

        const selectedGroupData = bannersWithTab.bannerWithTabGroups.find(
          (group) => group.label.toLowerCase() === currentGroup.toLowerCase(),
        );

        banners = selectedGroupData ? selectedGroupData.banners : [];

        return banners;
      }),
    );
  }

  onSelectGroup(label: string): void {
    this.selectedGroup.set(label);
  }

  protected onClickBanner(banner: BannerWithTabItem): void {
    const { id, name } = banner;
    this.promoAnalyticsService.onClickBanner({
      id,
      name: name,
      position: 'main_page',
    });
  }

  protected onBannerIntersect(banner: BannerWithTabItem): void {
    const { id, name } = banner;

    this.promoAnalyticsService.onShowBanner({
      id,
      name,
      position: 'main_page',
    });
  }
}
